import { LanguageChoice } from "@/components/language-choice";
import { Badge } from "@/components/ui/badge";
import { PageCanvas } from "@/components/page-canvas";
import {
  RecordDescription,
  RecordLink,
  RecordMetadata,
  RecordRow,
  RecordTitle,
} from "@/components/record-link";
import { Form, Head, Link } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

import {
  customerPortalCompanyProposalPath,
  customerPortalCompanyBillingReviewPath,
  customerPortalCompanyReschedulePath,
} from "@/routes";

type Company = { id: string; name: string };
export default function CustomerHistory({
  companies = [],
  company,
  recordings = [],
  pagination,
}: {
  companies?: Company[];
  company?: Company;
  pagination?: { page: number; pages: number };
  recordings?: {
    id: string;
    type: string;
    vehicle_history_path?: string;
    summary?: {
      vehicle: { make?: string; model?: string; registration_number?: string };
      customer_name?: string;
      starts_at?: string;
      created_at: string;
      time_zone: string;
      state?: string;
      pending_decision: boolean;
    };
    attachments?: { name: string; path: string }[];
  }[];
}) {
  const { t, i18n } = useTranslation();
  return (
    <PageCanvas as="main">
      <Head title={t("customer_portal.history")} />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-page font-display">
          {t("customer_portal.history")}
        </h1>
        <LanguageChoice />
        <Form action="/customer_portal/session" method="delete">
          <Button variant="outline" type="submit">
            {t("customer_portal.sign_out")}
          </Button>
        </Form>
      </div>
      <p className="text-muted-foreground">
        {t("customer_portal.history_help")}
      </p>
      {company ? (
        <Card>
          <CardHeader>
            <CardTitle>{company.name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {recordings.map((recording) => (
              <article
                key={recording.id}
                className="space-y-2 rounded-lg border p-4"
              >
                {recording.summary ? (
                  <>
                    <RecordTitle className="no-underline">
                      {[
                        recording.summary.vehicle.make,
                        recording.summary.vehicle.model,
                        recording.summary.vehicle.registration_number,
                      ]
                        .filter(Boolean)
                        .join(" · ") ||
                        recording.summary.customer_name ||
                        t("customer_portal.recording")}
                    </RecordTitle>
                    <RecordMetadata>
                      {t(`customer_portal.recording_${recording.type}`, {
                        defaultValue: t("customer_portal.recording"),
                      })}{" "}
                      ·{" "}
                      {new Intl.DateTimeFormat(i18n.language, {
                        dateStyle: "medium",
                        timeStyle: "short",
                        timeZone: recording.summary.time_zone,
                      }).format(
                        new Date(
                          recording.summary.starts_at ||
                            recording.summary.created_at,
                        ),
                      )}{" "}
                      · {recording.summary.time_zone}
                    </RecordMetadata>
                    {recording.summary.state && (
                      <RecordDescription>
                        {recording.type === "JobReschedule"
                          ? t(`reschedules.${recording.summary.state}_status`)
                          : t(
                              `${recording.type === "Job" ? "job_board.stages" : recording.type === "BillingReview" ? "billing_reviews.states" : "requests.states"}.${recording.summary.state}`,
                              { defaultValue: recording.summary.state },
                            )}
                      </RecordDescription>
                    )}
                    {recording.summary.pending_decision && (
                      <Badge>{t("access.pending_decision")}</Badge>
                    )}
                  </>
                ) : (
                  <RecordTitle className="no-underline">
                    {t(`customer_portal.recording_${recording.type}`, {
                      defaultValue: t("customer_portal.recording"),
                    })}
                  </RecordTitle>
                )}
                {recording.vehicle_history_path && (
                  <RecordLink
                    className="flex"
                    href={recording.vehicle_history_path}
                  >
                    {t("vehicle_reports.open")}
                  </RecordLink>
                )}
                {recording.type === "Proposal" && (
                  <RecordLink
                    className="flex"
                    href={customerPortalCompanyProposalPath(
                      company.id,
                      recording.id,
                    )}
                  >
                    {t("proposals.open")}
                  </RecordLink>
                )}
                {recording.type === "BillingReview" && (
                  <RecordLink
                    className="flex"
                    href={customerPortalCompanyBillingReviewPath(
                      company.id,
                      recording.id,
                    )}
                  >
                    {t("billing_reviews.open")}
                  </RecordLink>
                )}
                {recording.type === "JobReschedule" && (
                  <RecordLink
                    className="flex"
                    href={customerPortalCompanyReschedulePath(
                      company.id,
                      recording.id,
                    )}
                  >
                    {t("reschedules.open")}
                  </RecordLink>
                )}
                {recording.attachments?.map((attachment) => (
                  <a
                    key={attachment.path}
                    href={attachment.path}
                    className="block break-words underline"
                  >
                    {attachment.name}
                  </a>
                ))}
              </article>
            ))}
            {pagination && (
              <nav
                className="flex flex-wrap gap-4"
                aria-label={t("customer_portal.history")}
              >
                {pagination.page > 1 && (
                  <Link
                    className="underline"
                    href={`/customer_portal/companies/${company.id}/history?page=${pagination.page - 1}`}
                  >
                    {t("customers.previous")}
                  </Link>
                )}
                {pagination.page < pagination.pages && (
                  <Link
                    className="underline"
                    href={`/customer_portal/companies/${company.id}/history?page=${pagination.page + 1}`}
                  >
                    {t("customers.next")}
                  </Link>
                )}
              </nav>
            )}
            <RecordLink href="/customer_portal/history">
              {t("customer_portal.companies")}
            </RecordLink>
          </CardContent>
        </Card>
      ) : companies.length ? (
        companies.map((item) => (
          <Card key={item.id} className="py-0">
            <CardContent className="p-0">
              <RecordRow
                className="rounded-lg px-6 py-5"
                href={`/customer_portal/companies/${item.id}/history`}
              >
                <RecordTitle>{item.name}</RecordTitle>
              </RecordRow>
            </CardContent>
          </Card>
        ))
      ) : (
        <p>{t("customer_portal.empty")}</p>
      )}
    </PageCanvas>
  );
}
